<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :companyIndex="companyIndex"
    :countryCode="'SE'"
    :editableAccountFields="accountFields"
    :editableCompanyFields="companyFields"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useValidators } from "@/composables/useValidators";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";
import type { Account, Company, ContactFieldType } from "@/types";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accountData: Account;
  companyIndex?: number;
}>();

// Only the two events the template forwards are declared. SESignupFlow also binds
// @selectExistingCompany and @resetCompanySelection here; undeclared, those listeners fall
// through as attrs onto ContactReviewDisplayComponent, which does emit them. Declaring them
// would capture the listeners and cut that wiring.
defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
}>();

const { t } = useI18n();
const {
  required,
  swedishPersonalID,
  swedishIDorCompanyCode,
  personalName,
  phoneNumber,
  swedishVATNumber,
} = useValidators(t);

// `selectedCompany` throws when the route's company isn't in the account, so reading it here
// keeps the setup-time guard this component has always had. The value itself was returned from
// setup() but never rendered, and `<script setup>` has no equivalent dead binding to hold it.
const { selectedCompany } = useCompanyFinder(props.accountData);
void selectedCompany.value;

const accountFields: ContactFieldType[] = [
  {
    label: t("account.name"),
    id: "account-name",
    model: "name",
    type: "text",
    editable: true,
    validators: { required, personalName },
  },
  {
    label: t("account.phone_number"),
    id: "account-phone-number",
    model: "phone_number",
    type: "tel",
    editable: true,
    validators: { required, phoneNumber },
  },
  {
    label: t("account.registry_code"),
    id: "account-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: { required, swedishPersonalID },
  },
];

const companyFields: ContactFieldType[] = [
  {
    label: t("company.name"),
    id: "company-name",
    model: "name",
    type: "text",
    editable: true,
    validators: { required },
  },
  {
    label: t("company.registry_code"),
    id: "company-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: { required, swedishIDorCompanyCode },
  },
  {
    label: t("company.vat"),
    id: "company-vat",
    model: "vat",
    type: "text",
    editable: true,
    validators: { swedishVATNumber },
  },
];
</script>

<style scoped lang="scss"></style>
